import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import LandingSection from './LandingSection';
import LandingCard from './LandingCard';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import PlanPriceDisplay from '../subscription/PlanPriceDisplay';

const FALLBACK_PLANS = [
  {
    id: 'free',
    name: 'Free',
    slug: 'free',
    description: 'Try the core experience for small schools.',
    is_popular: false,
    trial_days: 0,
    currency: 'INR',
    pricing: {
      monthly: { default_price: 0, selling_price: 0 },
      half_yearly: { default_price: 0, selling_price: 0 },
      yearly: { default_price: 0, selling_price: 0 },
    },
    features: { limits: { max_students: 50 } },
  },
  {
    id: 'starter',
    name: 'Starter',
    slug: 'starter',
    description: 'For growing schools that want automation.',
    is_popular: true,
    trial_days: 7,
    currency: 'INR',
    pricing: {
      monthly: { default_price: 1999, selling_price: 1499 },
      half_yearly: { default_price: 11994, selling_price: 7999 },
      yearly: { default_price: 23988, selling_price: 13999 },
    },
    features: { limits: { max_students: 300 } },
  },
  {
    id: 'pro',
    name: 'Professional',
    slug: 'professional',
    description: 'Best for multi-branch schools and analytics.',
    is_popular: false,
    trial_days: 14,
    currency: 'INR',
    pricing: {
      monthly: { default_price: 3999, selling_price: 2999 },
      half_yearly: { default_price: 23994, selling_price: 15999 },
      yearly: { default_price: 47988, selling_price: 27999 },
    },
    features: { limits: { max_students: 1200 } },
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    slug: 'enterprise',
    description: 'Custom limits, onboarding and priority support.',
    is_popular: false,
    trial_days: 0,
    currency: 'INR',
    pricing: {
      monthly: { default_price: 0, selling_price: 0 },
      half_yearly: { default_price: 0, selling_price: 0 },
      yearly: { default_price: 0, selling_price: 0 },
    },
    features: { limits: { max_students: 0 } },
  },
];

function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

function CycleToggle({ cycle, onChange }) {
  const items = [
    { key: 'monthly', label: 'Monthly' },
    { key: 'half_yearly', label: 'Half‑Yearly' },
    { key: 'yearly', label: 'Yearly' },
  ];
  return (
    <div className="inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={cn(
            'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
            cycle === it.key ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white',
          )}
          onClick={() => onChange(it.key)}
          aria-pressed={cycle === it.key}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function normalizePlans(raw) {
  const plans = Array.isArray(raw) ? raw : [];
  return plans.map((p) => ({
    ...p,
    pricing: p.pricing || {
      monthly: { default_price: p.default_price_monthly, selling_price: p.price_monthly },
      half_yearly: { default_price: p.default_price_half_yearly, selling_price: p.price_half_yearly },
      yearly: { default_price: p.default_price_yearly, selling_price: p.price_yearly },
    },
  }));
}

export default function LandingPricing() {
  const [cycle, setCycle] = useState('monthly');
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [source, setSource] = useState('fallback');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('subscription/plans/compare-public');
        const loaded = normalizePlans(res.data?.data?.plans || []);
        if (!cancelled && loaded.length > 0) {
          setPlans(loaded.filter((p) => p.slug !== 'free'));
          setSource('api');
        }
      } catch {
        // keep fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visiblePlans = useMemo(() => {
    const filtered = plans.filter((p) => p && p.slug !== 'free');
    // keep order stable; popular first for landing visual hierarchy
    return [...filtered].sort((a, b) => Number(!!b.is_popular) - Number(!!a.is_popular));
  }, [plans]);

  return (
    <LandingSection
      id="pricing"
      title="Pricing"
      subtitle="Choose a plan that fits your school today—upgrade anytime."
      innerClassName=""
      withTopDivider
    >
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <CycleToggle cycle={cycle} onChange={setCycle} />
        <p className="text-xs text-white/45">
          {source === 'api' ? 'Live pricing from platform plans.' : 'Example pricing (login to see your exact plan options).'}
        </p>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {visiblePlans.map((plan) => (
          <LandingCard
            key={plan.id}
            className={cn(
              'flex h-full flex-col p-5',
              plan.is_popular && 'ring-1 ring-blue-500/30 border-blue-500/30',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-base font-semibold text-white">{plan.name}</p>
                <p className="mt-1 text-xs text-white/55 line-clamp-2">{plan.description}</p>
              </div>
              {plan.is_popular && (
                <Badge className="bg-blue-600/20 text-blue-200 border border-blue-500/30" variant="secondary">
                  Popular
                </Badge>
              )}
            </div>

            <div className="mt-4">
              <PlanPriceDisplay plan={plan} cycle={cycle} size="md" showCycleSuffix />
            </div>

            <ul className="mt-4 flex-1 space-y-2 text-xs text-white/60">
              {plan.features?.limits?.max_students ? (
                <li>Up to {plan.features.limits.max_students} students</li>
              ) : (
                <li>Custom limits</li>
              )}
              {plan.trial_days > 0 && <li>{plan.trial_days}-day free trial</li>}
              <li>Role-based access & secure login</li>
              <li>Reports & exports</li>
            </ul>

            <Button className="mt-5 w-full bg-blue-600 hover:bg-blue-700" asChild>
              <a href="#/login">Login to continue</a>
            </Button>
          </LandingCard>
        ))}
      </div>
    </LandingSection>
  );
}

